import { useEffect, useState } from "react";
import InforDirectionContainer from "../components/dashboard/InforDirectionContainer";
import SectionCard from "../components/dashboard/SectionCard";
import { useCrmStore } from "../stores/crm";
import { useButtonStore } from "../stores/components/button";
import { useTimeStore } from "../stores/time";
import { useStaffStore } from "../stores/staff";
import { usePaymentStore } from "../stores/payment";
import { useSocialStore } from "../stores/social";
import { useUtilsStore } from "../stores/components/utils";
import { useUserContext } from "../contexts/userContext";
import { showToastError } from "../services/toast";
import { getHolidayRequestsValidatedNotSent } from "../api/events";
import { useNavigate } from "react-router-dom";
import useModals from "../hooks/useModals";
import { PageShell } from "../components/ui/page/PageShell";

const Dashboard = () => {
  const { rights, firstname } = useUserContext();
  const { resetCrm, setCurrentPage } = useCrmStore();
  const { resetButton } = useButtonStore();
  const { resetTime } = useTimeStore();
  const { resetStaff } = useStaffStore();
  const { resetPayment } = usePaymentStore();
  const { resetSocial } = useSocialStore();
  const { setSearchTerm } = useUtilsStore();
  const navigate = useNavigate();
  const { setTab } = useTimeStore();
  const [nbHolidaysRequests, setNbHolidaysRequests] = useState(0);

  //Hooks
  const { modals, openModals } = useModals();
  useEffect(() => {
    resetCrm();
    setCurrentPage(1);
    resetButton();
    resetTime();
    resetStaff();
    resetPayment();
    setSearchTerm("");
    resetSocial();
  }, [
    resetCrm,
    resetButton,
    resetTime,
    resetStaff,
    resetPayment,
    resetSocial,
    setSearchTerm,
  ]);

  useEffect(() => {
    if (rights.includes(1004)) {
      getHolidayRequestsValidatedNotSent()
        .then((res) => {
          setNbHolidaysRequests(res.data.length);
        })
        .catch(() => {
          showToastError("Une erreur est survenue");
        });
    }
  }, []);

  const handleNavigateToValidateHolidays = () => {
    navigate("/time");
    setTab("HolidaysToValidate");
  };

  return (
    <PageShell scroll>
      {/* Bandeau d'accueil */}
      <section className="shrink-0 rounded-2xl border border-slate-200 bg-white px-4 py-5 shadow-sm sm:px-6 sm:py-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0">
            <p className="text-[11px] font-bold tracking-wider text-slate-400 uppercase">
              {import.meta.env.VITE_APP_TITLE}
            </p>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              Bonjour{firstname ? ` ${firstname}` : ""}
            </h1>
            <p className="mt-1.5 max-w-2xl font-Nunito text-sm text-slate-500">
              Votre solution complète de gestion d'entreprise. Accédez à tous
              vos outils en un clic.
            </p>
          </div>

          {/* Notification de demandes de congés en attente */}
          {rights.includes(1004) && nbHolidaysRequests > 0 && (
            <button
              type="button"
              onClick={() => handleNavigateToValidateHolidays()}
              className="group flex shrink-0 cursor-pointer items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-left transition-colors hover:bg-amber-100 focus-visible:ring-2 focus-visible:ring-amber-300 focus-visible:outline-none"
            >
              <span
                aria-hidden="true"
                className="flex size-9 shrink-0 items-center justify-center rounded-full bg-amber-500 text-white"
              >
                <svg
                  className="size-4.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15 17h5l-1.4-1.4A2 2 0 0118 14.2V11a6 6 0 10-12 0v3.2a2 2 0 01-.6 1.4L4 17h5m6 0v1a3 3 0 11-6 0v-1"
                  />
                </svg>
              </span>
              <span className="min-w-0">
                <span className="block font-Nunito text-sm font-bold text-amber-900">
                  {nbHolidaysRequests} nouvelle
                  {nbHolidaysRequests > 1 ? "s" : ""} demande
                  {nbHolidaysRequests > 1 ? "s" : ""} de congé
                  {nbHolidaysRequests > 1 ? "s" : ""}
                </span>
                <span className="block font-Nunito text-xs text-amber-700">
                  Cliquez pour les traiter
                </span>
              </span>
            </button>
          )}
        </div>
      </section>

      {/* Accès aux modules */}
      <section>
        <h2 className="mb-2.5 px-1 text-[11px] font-bold tracking-wider text-slate-400 uppercase">
          Modules
        </h2>
        <SectionCard />
      </section>

      <section className="mt-auto">
        <h2 className="mb-2.5 px-1 text-[11px] font-bold tracking-wider text-slate-400 uppercase">
          Information
        </h2>
        <InforDirectionContainer />
      </section>

      {/* Affichage des modales en fonction de openModals */}
      {Object.keys(modals).map((modalId) =>
        openModals[modalId] ? <div key={modalId}>{modals[modalId]}</div> : null,
      )}
    </PageShell>
  );
};

export default Dashboard;
